import { type OrderBy, queryBuilder } from '@inpart-io/devtools-iap';
import { GET_BY_ID, RETRIEVE_BY_QUERY } from '@inpart-io/devtools-vue';
import {
    type IFieldCustomValue,
    type ISharedFieldCustomValue,
    sharedFieldCustomListValueProperties,
    sharedFieldCustomValuesManager
} from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { LIMIT_TOTAL_DISPLAY, MAX_RESULTS } from '@/constants/field-custom-value.constants';

import { RECEIVE_BY_LIST_ID } from './mutations';

export const RETRIEVE_TYPEAHEAD = 'RETRIEVE_TYPEAHEAD';
export const RETRIEVE_BY_LIST_ID = 'RETRIEVE_BY_LIST_ID';

export const actions: ActionTree<ISharedFieldCustomValue, unknown> = {
    async [RETRIEVE_TYPEAHEAD](
        context,
        { searchTerm, listId, hasPosition }: { searchTerm: string; listId: string; hasPosition?: boolean }
    ): Promise<{ response: IFieldCustomValue[]; totalCount: number }> {
        let response: IFieldCustomValue[];
        let totalCount: number;
        const comparator = queryBuilder.cmp.equalTo('listId', listId);
        if (searchTerm?.length > 1) {
            response = await sharedFieldCustomValuesManager
                .typeahead(searchTerm, MAX_RESULTS, queryBuilder.op.and(comparator), sharedFieldCustomListValueProperties.DEFAULT)
                .then((results) => {
                    return results.map((result) => {
                        return result.dataObject;
                    });
                });
        } else {
            totalCount = await sharedFieldCustomValuesManager.count(
                queryBuilder.build(queryBuilder.op.and(comparator), 0, undefined, [
                    {
                        attribute: hasPosition ? 'position' : 'label',
                        direction: 'ASC'
                    }
                ])
            );
            let maxResults: number;
            if (totalCount > LIMIT_TOTAL_DISPLAY) {
                maxResults = MAX_RESULTS;
            } else {
                maxResults = LIMIT_TOTAL_DISPLAY;
            }
            const cleanSearchTerm = searchTerm?.length > 0 ? searchTerm : '';
            response = (
                await context.dispatch(RETRIEVE_BY_QUERY, {
                    query: queryBuilder.build(queryBuilder.op.and(comparator, queryBuilder.cmp.startWithIgnoreCase('label', cleanSearchTerm)), 0, maxResults, [
                        {
                            attribute: hasPosition ? 'position' : 'label',
                            direction: 'ASC'
                        }
                    ]),
                    properties: sharedFieldCustomListValueProperties.DEFAULT
                })
            ).map((dobjId: number) => {
                return context.getters[GET_BY_ID](dobjId);
            });
        }
        return { response, totalCount };
    },
    async [RETRIEVE_BY_LIST_ID](context, { listId, properties, orderBy }: { listId: string; properties: string; orderBy?: OrderBy[] }): Promise<void> {
        if (!orderBy?.length) {
            orderBy = [
                { direction: 'ASC', attribute: 'position' },
                { direction: 'ASC', attribute: 'label' }
            ];
        }

        const idList = await context.dispatch(RETRIEVE_BY_QUERY, {
            query: queryBuilder.build(queryBuilder.op.and(queryBuilder.cmp.equalTo('listId', listId)), 0, null, orderBy),
            properties
        });
        context.commit(RECEIVE_BY_LIST_ID, { listId, idList });
    }
};
